import moment from 'moment';
import _ from 'lodash';
import { valueBuilder } from '../../../../core';
import { EXPIRED_SHOPIFY_SUBSCRIPTION_STATUSES } from '../../../constants';
import { DATE_FIELDS, DATE_TIME_FIELDS, FIELDS } from './constants';

export const createInitialUpdatedValues = (company = {}) => {
    const notificationData = company.notificationData || {};

    return {
        trialExpire: company.trialExpire,
        notifiedAboutCompanyRemovingDate: notificationData.notifiedAboutCompanyRemovingDate,
        showUsedTicketsCount: company.showUsedTicketsCount,
        billingUsage: company.billingUsage,
        isOldSubscription: company.isOldSubscription,
        failedSubscription: company.failedSubscription,
        subscriptionExpiredDate: company.subscriptionExpiredDate,
        dataLifetime: company.dataLifetime,
        ordersDataLifetime: company.ordersDataLifetime,
        feedbacksDataLifetime: company.feedbacksDataLifetime,
        archiveThreadsLifetime: company.archiveThreadsLifetime,
        deletedDataLifetime: company.deletedDataLifetime,
        isManuallyVerified: company.isManuallyVerified,
        isVerified: company.isVerified,
        showPopupNotifications: company.showPopupNotifications,
        stripeSubscriptionId: company.stripeSubscriptionId,
        region: company.region,
        appLanguage: company.appLanguage,
        integrationsCount: company.integrationsCount,
        endlessIntegrations: company.endlessIntegrations
    };
};

export const getFormData = (company = {}, updatedValues = {}) => ({
    ...company,
    ...updatedValues,
    notificationData: {
        ...company.notificationData,
        notifiedAboutCompanyRemovingDate: updatedValues.notifiedAboutCompanyRemovingDate
    }
});

export const hasCompanyChanges = (company = null, updatedValues = {}, fieldNames = null) => {
    if (!company) return false;
    const valuesToCompare = fieldNames ? _.pick(updatedValues, fieldNames) : updatedValues;

    return !_.every(_.keys(valuesToCompare), key => {
        if (key === FIELDS.integrationsCount && company[key] && !valuesToCompare[key]) {
            return false;
        }
        if (DATE_FIELDS.includes(key)) {
            return valueBuilder.getDate(valuesToCompare[key]) === valueBuilder.getDate(company[key]);
        }
        if (DATE_TIME_FIELDS.includes(key)) {
            return valueBuilder.getFullTime(valuesToCompare[key]) === valueBuilder.getFullTime(company[key]);
        }
        return valuesToCompare[key] === company[key];
    });
};

export const buildUpdatePayload = (company = {}, updatedValues = {}, fieldNames = null) => {
    const valuesToUpdate = fieldNames ? _.pick(updatedValues, fieldNames) : updatedValues;
    const { notifiedAboutCompanyRemovingDate, ...rest } = valuesToUpdate;
    const hasNotificationDate = _.has(valuesToUpdate, FIELDS.notifiedAboutCompanyRemovingDate);

    return {
        ...company,
        ...rest,
        notificationData: {
            ...company.notificationData,
            ...(hasNotificationDate ? { notifiedAboutCompanyRemovingDate } : {})
        }
    };
};

export const getSubscriptionProvider = data => {
    if (_.get(data, 'shopifyBillingInfo')) return 'shopify';
    if (data.isManualSubscribed) return 'manual';
    if (data.stripeSubscriptionId || data.paymentCustomerId || data.subscriptionPlanId) return 'stripe';
    return 'none';
};

export const getSubscriptionPlanName = (data, subscriptionPlans, t) => {
    const selectedPlan = _.find(subscriptionPlans, subscriptionPlan => (
        String(subscriptionPlan.id).toLowerCase() === String(data.subscriptionPlanId).toLowerCase()
    ));

    if (_.get(data, 'subscriptionPlan.name')) return data.subscriptionPlan.name;
    if (selectedPlan) return selectedPlan.name;
    if (data.trialExpire && moment(data.trialExpire).isAfter(moment())) {
        return t('companies.details.summary.trial');
    }
    return t('companies.details.summary.noPlan');
};

export const getSubscriptionStatus = (data, t) => {
    const shopifyStatus = _.get(data, 'shopifyBillingInfo.status', '').toLowerCase();
    const isManualExpired = data.isManualSubscribed && (!data.manualSubscriptionExpire ||
        moment(data.manualSubscriptionExpire).isSameOrBefore(moment()));
    const isShopifyExpired = EXPIRED_SHOPIFY_SUBSCRIPTION_STATUSES.includes(shopifyStatus);
    const isTrialExpired = !data.subscriptionPlanId && data.trialExpire &&
        moment(data.trialExpire).isSameOrBefore(moment());

    if (data.failedSubscription) return t('companies.details.status.failed');
    if (isManualExpired || isShopifyExpired) return t('companies.details.status.expired');
    if (data.subscriptionPlanId || shopifyStatus === 'active') return t('companies.details.status.active');
    if (isTrialExpired) return t('companies.details.status.expired');
    if (data.trialExpire && moment(data.trialExpire).isAfter(moment())) {
        return t('companies.details.status.trial');
    }
    return t('companies.details.status.none');
};
